import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { EuiButton, EuiButtonIcon, EuiCheckbox, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText, EuiTitle, EuiToolTip } from '@elastic/eui'

import { PostalCodeTarget } from 'api/interfaces'
import { AIPlanCampaignBuilder, AIPlanCampaignCopyStatus, AIPlanStatus } from 'api/interfaces/AIPlan'
import AgencyV2Modal from 'features/planner/components/AgencyV2Modal'
import SparklesIcon from 'features/planner/components/SparklesIcon'
import BackgroundContentCard from 'features/planner/pages/MasterCampaignPlanPage/components/BackgroundContentCard'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'

import { CampaignSectionType } from './EditAIPlanCampaignSectionFlyout'

interface AIPlanCampaignCardProps {
  builder: AIPlanCampaignBuilder
  planStatus: AIPlanStatus
  onEditBasicsClick?: () => void
  onEditNameClick?: () => void
  onEditSectionClick?: (sectionType: CampaignSectionType) => void
  onRemoveCampaignClick?: () => Promise<void>
  onAIEditSection?: (sectionLabel: string, campaignSectionName: string) => void
  onAIEditCampaign?: () => void
  onFullEditClick?: () => void
  onProjectionSettingsClick?: () => void
  onCopyCampaignClick?: () => Promise<void>
  onViewInDspClick?: (dspCampaignId: string) => void
  isRemovingCampaign: boolean
  isCopyingCampaign: boolean
  showActions?: boolean
}

const AIPlanCampaignCard: React.FC<AIPlanCampaignCardProps> = ({ builder, planStatus, onEditBasicsClick, onEditNameClick, onEditSectionClick, onRemoveCampaignClick, onAIEditSection, onAIEditCampaign, onFullEditClick, onProjectionSettingsClick, onCopyCampaignClick, onViewInDspClick, isRemovingCampaign, isCopyingCampaign, showActions = true }) => {
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false)
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<{ city: string; postalCodes: PostalCodeTarget[] }[]>([])
  const [isExpanded] = useState(true)

  useEffect(() => {
    const grouped: { city: string; postalCodes: PostalCodeTarget[] }[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!grouped.find(g => g.city === city)) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup?.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  const onConfirmRemoveCampaignClick = async () => {
    await onRemoveCampaignClick?.()
    setIsRemoveModalVisible(false)
  }

  const onConfirmCopyClick = async () => {
    await onCopyCampaignClick?.()
    setIsCopyModalVisible(false)
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const isNotCopied = !builder.copyStatus || builder.copyStatus === AIPlanCampaignCopyStatus.NotCopied
  const canEdit = showActions && planStatus !== AIPlanStatus.CopiedToDsp && isNotCopied
  const campaignTypeDisplay = builder.isOffAdCritter && builder.offAdCritterName ? builder.offAdCritterName : builder.campaignType

  const predefinedOffAdCritterPlatforms = ['Facebook', 'Google', 'Instagram', 'LinkedIn', 'TikTok']
  const isCustomOffAdCritter = builder.isOffAdCritter && !predefinedOffAdCritterPlatforms.includes(builder.offAdCritterName)

  const shouldShowCopyButton = !builder.isOffAdCritter && planStatus === AIPlanStatus.Ready && isNotCopied
  const shouldShowViewInDspButton = builder.copyStatus === AIPlanCampaignCopyStatus.Copied && builder.dspCampaignId

  return (
    <>
      <div id={`campaign-${builder.id}`}>
        <EuiFlexGroup>
          <EuiFlexItem>
            <EuiPanel hasShadow={true} style={{ backgroundColor: '#ede7dd', padding: '3rem 5rem' }}>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                    <EuiFlexItem grow={false}>
                      <EuiTitle size='s'>
                        <p>{builder.name}</p>
                      </EuiTitle>
                    </EuiFlexItem>
                    {canEdit && onEditNameClick && !isCustomOffAdCritter && (
                      <EuiFlexItem grow={false}>
                        <EuiToolTip content='Edit Name'>
                          <EuiButtonIcon iconType='visText' style={{ color: '#0071c2' }} onClick={onEditNameClick} aria-label='Edit campaign name' size='s' />
                        </EuiToolTip>
                      </EuiFlexItem>
                    )}
                    {canEdit && isCustomOffAdCritter && onFullEditClick && (
                      <EuiFlexItem grow={false}>
                        <EuiToolTip content='Edit Campaign'>
                          <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onFullEditClick} aria-label='Edit campaign' size='s' />
                        </EuiToolTip>
                      </EuiFlexItem>
                    )}
                    {showActions && onAIEditCampaign && !isCustomOffAdCritter && (
                      <EuiFlexItem grow={false}>
                        <EuiToolTip content='AI Edit Entire Campaign'>
                          <EuiButtonIcon iconType={SparklesIcon} onClick={onAIEditCampaign} aria-label='AI Edit Campaign' size='s' />
                        </EuiToolTip>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  {canEdit && (
                    <EuiFlexGroup justifyContent='flexEnd' gutterSize='s' responsive={false}>
                      <EuiFlexItem grow={false}>
                        <EuiButtonIcon iconType='trash' style={{ color: '#BD271E' }} onClick={() => setIsRemoveModalVisible(true)} aria-label='Remove campaign' />
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiSpacer size='m' />
              <EuiFlexGroup>
                <EuiFlexItem>
                  <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                    <EuiFlexItem grow={false}>
                      <span><b>Campaign Type:</b> {campaignTypeDisplay}</span>
                    </EuiFlexItem>
                    {canEdit && onEditBasicsClick && !isCustomOffAdCritter && (
                      <EuiFlexItem grow={false}>
                        <EuiToolTip content='Edit Basics'>
                          <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={onEditBasicsClick} aria-label='Edit campaign basics' size='s' />
                        </EuiToolTip>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                  <span>
                    <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                    {builder.endDate && (
                      <>
                        <br />
                        <b>Ends:</b> {moment(builder.endDate).format('MMMM Do YYYY')}
                      </>
                    )}
                    <br />
                    <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)})` : ''}
                  </span>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  {shouldShowCopyButton && (
                    <EuiButton size='s' color='primary' fill onClick={() => setIsCopyModalVisible(true)}>
                      Copy to DSP
                    </EuiButton>
                  )}
                  {shouldShowViewInDspButton && (
                    <EuiButton size='s' color='primary' onClick={() => onViewInDspClick?.(builder.dspCampaignId!)}>
                      View in DSP
                    </EuiButton>
                  )}
                </EuiFlexItem>
              </EuiFlexGroup>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <span>
                    <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                    {builder.isTotalBudget ? ' (total)' : (
                      <>
                        {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                        {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                      </>
                    )}
                  </span>
                </EuiFlexItem>
              </EuiFlexGroup>

              {canEdit && builder.isOffAdCritter && onProjectionSettingsClick && (
                <>
                  <EuiSpacer size='m' />
                  <EuiCheckbox
                    id={`includeInProjections-${builder.id}`}
                    label='Include in Projections'
                    checked={builder.includeInProjections}
                    onChange={() => onProjectionSettingsClick()}
                  />
                </>
              )}

              <EuiSpacer size='l' />

              {builder.longSummary && (
                <EuiFlexGroup>
                  <EuiFlexItem>
                    <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                      <EuiFlexItem grow={false}><b>Summary</b></EuiFlexItem>
                      {canEdit && onEditSectionClick && !isCustomOffAdCritter && (
                        <EuiFlexItem grow={false}>
                          <EuiToolTip content='Edit'>
                            <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => onEditSectionClick('summary')} aria-label='Edit Summary' size='s' />
                          </EuiToolTip>
                        </EuiFlexItem>
                      )}
                      {showActions && onAIEditSection && !isCustomOffAdCritter && (
                        <EuiFlexItem grow={false}>
                          <EuiToolTip content='AI Edit'>
                            <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditSection(`${builder.name} - Summary`, 'summary')} aria-label='AI Edit Summary' size='s' />
                          </EuiToolTip>
                        </EuiFlexItem>
                      )}
                    </EuiFlexGroup>
                    <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                    <EuiSpacer size='s' />
                    <EuiText size='s'>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{builder.longSummary}</p>
                    </EuiText>
                    <EuiSpacer size='l' />
                  </EuiFlexItem>
                </EuiFlexGroup>
              )}

              {isExpanded && (
                <div>
                  <EuiSpacer size='l' />

                  {builder.seasonalitySummary && (
                    <EuiFlexGroup>
                      <EuiFlexItem>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                            <EuiFlexItem grow={false}><b>Timing</b></EuiFlexItem>
                            {canEdit && onEditSectionClick && !isCustomOffAdCritter && (
                              <EuiFlexItem grow={false}>
                                <EuiToolTip content='Edit'>
                                  <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => onEditSectionClick('timing')} aria-label='Edit Timing' size='s' />
                                </EuiToolTip>
                              </EuiFlexItem>
                            )}
                            {showActions && onAIEditSection && !isCustomOffAdCritter && (
                              <EuiFlexItem grow={false}>
                                <EuiToolTip content='AI Edit'>
                                  <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditSection(`${builder.name} - Timing`, 'timing')} aria-label='AI Edit Timing' size='s' />
                                </EuiToolTip>
                              </EuiFlexItem>
                            )}
                          </EuiFlexGroup>
                          <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                          <EuiText size='s'>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{builder.seasonalitySummary}</p>
                          </EuiText>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  )}

                  <EuiFlexGroup alignItems='stretch'>
                    {(builder.geoSummary || groupedPostalCodes.length > 0) && (
                      <EuiFlexItem grow={4}>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <div>
                            <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                              <EuiFlexItem grow={false}><b>Geography</b></EuiFlexItem>
                              {canEdit && onEditSectionClick && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='Edit'>
                                    <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => onEditSectionClick('geo')} aria-label='Edit Geography' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                              {showActions && onAIEditSection && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='AI Edit'>
                                    <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditSection(`${builder.name} - Geography`, 'geography')} aria-label='AI Edit Geography' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                            </EuiFlexGroup>
                            <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                            {builder.geoSummary && (
                              <>
                                <EuiSpacer size='s' />
                                <EuiText size='s'>
                                  <p style={{ whiteSpace: 'pre-wrap' }}>{builder.geoSummary}</p>
                                </EuiText>
                              </>
                            )}
                          </div>
                          <EuiSpacer size='m' />
                          <ul style={{ marginLeft: '1rem' }}>
                            {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                              <li key={index}>
                                <b>{city}</b>
                                <EuiSpacer size='xs' />
                                {postalCodes.map((postalCode, pcIndex) => (
                                  <ul key={pcIndex}>
                                    <li>{postalCode.code}</li>
                                  </ul>
                                ))}
                                <EuiSpacer size='s' />
                              </li>
                            ))}
                          </ul>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    )}
                    {builder.isOffAdCritter && builder.offAdCritterName === 'Google' && (builder.keywordDescription || (builder.keywords?.length ?? 0) > 0) && (
                      <EuiFlexItem grow={6}>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <div>
                            <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                              <EuiFlexItem grow={false}><b>Keywords</b></EuiFlexItem>
                              {canEdit && onEditSectionClick && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='Edit'>
                                    <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => onEditSectionClick('keywords')} aria-label='Edit Keywords' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                              {showActions && onAIEditSection && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='AI Edit'>
                                    <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditSection(`${builder.name} - Keywords`, 'keywords')} aria-label='AI Edit Keywords' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                            </EuiFlexGroup>
                            <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                            {builder.keywordDescription && (
                              <>
                                <EuiSpacer size='s' />
                                <EuiText size='s'>
                                  <p style={{ whiteSpace: 'pre-wrap' }}>{builder.keywordDescription}</p>
                                </EuiText>
                              </>
                            )}
                          </div>
                          <EuiSpacer size='m' />
                          {(builder.keywords?.length ?? 0) > 0 && (
                            <div>
                              <b>Sample Keywords</b>
                              <EuiSpacer size='s' />
                              {builder.keywords!.map((keyword, index) => (
                                <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                                  <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#343741', marginRight: '10px', marginTop: '7px', flexShrink: 0 }} />
                                  <EuiText size='s'>{keyword}</EuiText>
                                </div>
                              ))}
                            </div>
                          )}
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    )}
                    {!(builder.isOffAdCritter && builder.offAdCritterName === 'Google') && (builder.audienceSummary || (builder.segmentTargets?.length ?? 0) > 0 || (builder.segmentDescriptions?.length ?? 0) > 0) && (
                      <EuiFlexItem grow={6}>
                        <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                          <div>
                            <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                              <EuiFlexItem grow={false}><b>{builder.isOffAdCritter ? 'Audience' : 'Audience Segments'}</b></EuiFlexItem>
                              {canEdit && onEditSectionClick && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='Edit'>
                                    <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => onEditSectionClick('audience')} aria-label='Edit Audience Segments' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                              {showActions && onAIEditSection && !isCustomOffAdCritter && (
                                <EuiFlexItem grow={false}>
                                  <EuiToolTip content='AI Edit'>
                                    <EuiButtonIcon iconType={SparklesIcon} onClick={() => onAIEditSection(`${builder.name} - ${builder.isOffAdCritter ? 'Audience' : 'Audience Segments'}`, builder.isOffAdCritter ? 'audience' : 'audienceSegments')} aria-label='AI Edit Audience Segments' size='s' />
                                  </EuiToolTip>
                                </EuiFlexItem>
                              )}
                            </EuiFlexGroup>
                            <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                            {builder.audienceSummary && (
                              <>
                                <EuiSpacer size='s' />
                                <EuiText size='s'>
                                  <p style={{ whiteSpace: 'pre-wrap' }}>{builder.audienceSummary}</p>
                                </EuiText>
                              </>
                            )}
                          </div>
                          <EuiSpacer size='m' />
                          <ul style={{ marginLeft: '1rem' }}>
                            {builder.isOffAdCritter ? (
                              builder.segmentDescriptions?.map((description, index) => (
                                <li key={index}>
                                  <EuiText size='s'>{description}</EuiText>
                                  <EuiSpacer size='s' />
                                </li>
                              ))
                            ) : (
                              builder.segmentTargets?.map((segment, index) => (
                                <li key={index}>
                                  <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b> (Segment {segment.id})
                                  <div>
                                    <EuiSpacer size='xs' />
                                    {segment.name}
                                    <EuiSpacer size='m' />
                                  </div>
                                </li>
                              ))
                            )}
                          </ul>
                        </BackgroundContentCard>
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </div>
              )}
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>

      {isRemoveModalVisible && (
        <AgencyV2Modal isLoading={isRemovingCampaign} title='Remove Campaign' onClose={() => setIsRemoveModalVisible(false)} confirmButtonText='Remove' onConfirmButtonClick={onConfirmRemoveCampaignClick} showCancelButton>
          <EuiText>
            Campaign: <strong>{builder.name}</strong> will be removed from the plan.
          </EuiText>
        </AgencyV2Modal>
      )}

      {isCopyModalVisible && (
        <AgencyV2Modal isLoading={isCopyingCampaign} title='Copy Campaign to DSP' onClose={() => setIsCopyModalVisible(false)} confirmButtonText='Copy' onConfirmButtonClick={onConfirmCopyClick} showCancelButton>
          <EuiText>
            <p>This will copy the campaign <strong>{builder.name}</strong> to AdCritter DSP where you will have full editing control.</p>
          </EuiText>
          <EuiSpacer size='m' />
          <EuiText>
            <p>Are you ready to copy this campaign to the DSP?</p>
          </EuiText>
        </AgencyV2Modal>
      )}
    </>
  )
}

export default AIPlanCampaignCard
